import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, page, cardScroll, btn, btnDisabled } from './AdminUsers';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// Bulk-create is_active:false accounts from a CSV (username,phone,email —
// at least one of phone/email required per row). Each gets a claim link
// (see bulkUsers.js/claimAccount.js backend headers) sent over every
// channel on file — SMS and email, both if both were given — clicking it
// (from either) walks them through phone/email verification, setting
// their own password, and TOTP setup before the account actually
// activates.
//
// UPDATED 2026-08-05: restyled onto the same light glass-card shell as
// AdminUsers.jsx/User Directory (page/card/btn/btnDisabled imported from
// there) — this used to have its own bespoke dark "mirror" glass look,
// inconsistent with the rest of the admin section.

const rowBg = '#fff';
const rowBorder = '1px solid #eee';

// Shared between both tabs — the {created, skipped} response shape from
// /upload and /upload-office is identical.
function UploadResultSummary({ data }) {
  if (!data) return null;
  return (
    <div style={{ marginBottom: '24px', border: '1px solid #eee', borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', background: rowBg, borderBottom: rowBorder, fontSize: '13px', color: '#1a1a1a', fontWeight: '600' }}>
        {data.created.length} account{data.created.length === 1 ? '' : 's'} created
        {data.skipped.length > 0 && `, ${data.skipped.length} row${data.skipped.length === 1 ? '' : 's'} skipped`}
      </div>
      {data.created.some((c) => c.note) && (
        <div style={{ padding: '12px 16px', background: '#FFF7E6', borderBottom: rowBorder, fontSize: '12px', color: '#7A5900' }}>
          {data.created.filter((c) => c.note).map((c) => (
            <div key={c.row} style={{ marginBottom: '4px' }}>
              Row {c.row} ({c.username}): {c.note}
            </div>
          ))}
        </div>
      )}
      {data.skipped.length > 0 && (
        <div style={{ padding: '12px 16px', background: '#FDECEC', fontSize: '12px', color: '#B3261E' }}>
          {data.skipped.map((s) => (
            <div key={s.row} style={{ marginBottom: '4px' }}>
              Row {s.row} ({s.username || 'no username'}): {s.reason}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminBulkUsers() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('officers'); // 'officers' | 'office'

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Separate state for the Office Accounts tab — kept distinct from the
  // Officer Accounts tab above rather than shared, since both forms have
  // their own file/result independent of whichever tab is active.
  const [officeFile, setOfficeFile] = useState(null);
  const [officeUploading, setOfficeUploading] = useState(false);
  const [officeResult, setOfficeResult] = useState(null);

  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [resendingPk, setResendingPk] = useState(null);

  // Guard: only bescom-super-admins. Two cases, not one — see
  // AdminApprovals.jsx's own comment on this same pattern.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    const groups = user.profile?.groups || [];
    if (!groups.includes('bescom-super-admins')) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchPending() {
    setLoadingPending(true);
    try {
      const res = await fetch(`${API_BASE}/admin/bulk-users/pending`, {
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();
      setPending(data.results || []);
    } catch (e) {
      // best-effort — upload still works even if this listing fails
    } finally {
      setLoadingPending(false);
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE}/admin/bulk-users/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Upload failed.');
        return;
      }
      setResult(data);
      setFile(null);
      fetchPending();
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setUploading(false);
    }
  }

  async function handleUploadOffice(e) {
    e.preventDefault();
    if (!officeFile) return;
    setOfficeUploading(true);
    setError(null);
    setOfficeResult(null);
    try {
      const formData = new FormData();
      formData.append('file', officeFile);
      const res = await fetch(`${API_BASE}/admin/bulk-users/upload-office`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Upload failed.');
        return;
      }
      setOfficeResult(data);
      setOfficeFile(null);
      fetchPending();
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setOfficeUploading(false);
    }
  }

  async function handleResend(pk) {
    setResendingPk(pk);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/bulk-users/${pk}/resend-invite`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not resend invite.');
        return;
      }
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setResendingPk(null);
    }
  }

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
      <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
      <AdminSidebar active="bulk-users" />
      <div style={cardScroll}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 4px' }}>
          Bulk User Onboarding
        </h1>

        <div style={{ display: 'flex', gap: '8px', margin: '16px 0 20px' }}>
          <button
            type="button"
            onClick={() => setActiveTab('officers')}
            style={{
              padding: '8px 18px', borderRadius: '8px', border: 'none',
              fontWeight: '600', fontSize: '13px', cursor: 'pointer',
              background: activeTab === 'officers' ? '#009FE3' : '#F0F0F0',
              color: activeTab === 'officers' ? '#fff' : '#555',
            }}>
            Officer's Accounts
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('office')}
            style={{
              padding: '8px 18px', borderRadius: '8px', border: 'none',
              fontWeight: '600', fontSize: '13px', cursor: 'pointer',
              background: activeTab === 'office' ? '#009FE3' : '#F0F0F0',
              color: activeTab === 'office' ? '#fff' : '#555',
            }}>
            Office Accounts
          </button>
        </div>

        {error && (
          <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {activeTab === 'officers' ? (
          <>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
              Upload a CSV with columns <code>username, phone, email, account_type, Designation, Role, Groups</code> (each row needs
              a phone or an email, or both — the rest are optional). Accounts are created inactive — each person gets an
              activation link (by SMS and email, whichever was given — both if both were) to verify their own contact info, set a password,
              and choose how they'll sign in (authenticator app, passkey, or SMS code) before the account goes live. Every account is added to{' '}
              <code>bescom-officers</code>; Role additionally maps to <code>bescom-section-officers</code> (Section_Officer),{' '}
              <code>bescom-aee</code> (Assistant Executive Engineer), or <code>bescom-ee</code> (Executive Engineer) if recognized.
              <code>Groups</code> adds any number of further groups, separated by <code>;</code> (e.g. <code>group-a;group-b</code>) — a row naming a
              group that doesn't exist, or <code>bescom-super-admins</code>, is skipped with the reason shown.
            </p>

            <a
              href="/samples/sample-SSO-bulk-officers-user-creation.csv"
              download
              style={{ display: 'inline-block', fontSize: '13px', color: '#666', fontWeight: '600', marginBottom: '16px' }}
            >
              ⬇ Download sample CSV
            </a>

            <form onSubmit={handleUpload} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{ color: '#1a1a1a', fontSize: '13px' }}
              />
              <button type="submit" disabled={uploading || !file} style={(uploading || !file) ? btnDisabled : btn}>
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </form>

            <UploadResultSummary data={result} />
          </>
        ) : (
          <>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
              Upload a CSV with columns <code>username, phone, email, account_type, Office, Group</code> (each row needs
              a phone or an email, or both). <code>Office</code> names an existing hierarchy "office" entry — a Zone,
              Circle, or Division with nothing beneath it (see Hierarchy Management) — as{' '}
              <code>"&lt;name&gt;(&lt;level&gt;_Office)"</code>, e.g. <code>JAYANAGAR(Div_Office)</code> or{' '}
              <code>CTAZ(Zone_Office)</code>. <code>Group</code> is the exact Authentik group name to add the account
              to — used as-is, no other group is added automatically. Designation is set to <code>Office Account</code>{' '}
              on every row.
            </p>

            <a
              href="/samples/sample-SSO-bulk-office-accounts-creation.csv"
              download
              style={{ display: 'inline-block', fontSize: '13px', color: '#666', fontWeight: '600', marginBottom: '16px' }}
            >
              ⬇ Download sample CSV
            </a>

            <form onSubmit={handleUploadOffice} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setOfficeFile(e.target.files?.[0] || null)}
                style={{ color: '#1a1a1a', fontSize: '13px' }}
              />
              <button type="submit" disabled={officeUploading || !officeFile} style={(officeUploading || !officeFile) ? btnDisabled : btn}>
                {officeUploading ? 'Uploading…' : 'Upload'}
              </button>
            </form>

            <UploadResultSummary data={officeResult} />
          </>
        )}

        <h3 style={{ fontSize: '15px', color: '#1a1a1a', marginBottom: '4px' }}>
          Pending invites{!loadingPending && ` (${pending.length})`}
        </h3>
        <p style={{ fontSize: '12px', color: '#999', marginBottom: '12px' }}>
          Includes accounts stuck mid-setup — activated but abandoned before choosing a sign-in
          method, which otherwise leaves them with no way to log in and no way for you to notice.
        </p>
        {loadingPending ? (
          <p style={{ color: '#666', fontSize: '14px' }}>Loading…</p>
        ) : pending.length === 0 ? (
          <p style={{ color: '#666', fontSize: '14px' }}>No pending invites — everyone uploaded has activated, or nothing's been uploaded yet.</p>
        ) : (
          <div style={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'hidden' }}>
            {pending.map((u) => (
              <div
                key={u.pk}
                style={{
                  padding: '14px 16px', background: rowBg, borderBottom: rowBorder,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a1a' }}>{u.username}</span>
                    {u.stuck && (
                      <span style={{
                        fontSize: '11px', fontWeight: '600', color: '#7A5900',
                        background: '#FFF7E6', padding: '2px 8px', borderRadius: '999px',
                      }}>
                        Stuck at sign-in method setup
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                    {u.phone || 'no phone'} · {u.email || 'no email'}
                  </div>
                </div>
                <button
                  onClick={() => handleResend(u.pk)}
                  disabled={resendingPk === u.pk}
                  style={resendingPk === u.pk ? { ...btn, width: 'auto', marginTop: 0, opacity: 0.6, cursor: 'not-allowed' } : { ...btn, width: 'auto', marginTop: 0 }}
                >
                  {resendingPk === u.pk ? 'Resending…' : u.stuck ? 'Resend Setup Link' : 'Resend Invite'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
      </div>
    </>
  );
}
